import 'mapbox-gl/dist/mapbox-gl.css';
import './Heatmap.css';
import interpolate from 'color-interpolate';
import React, { useRef, useState, useMemo } from 'react';
import { Map, MapRef, Marker } from 'react-map-gl';
import Supercluster from 'supercluster';

import { Stack } from '../Stack/Stack';
import { Spinner } from '../Spinner/Spinner';
import { useSupercluster } from '../../util/use-supercluster';

export interface Cluster {
  id: string;
  count: number;
  isCountry: boolean;
  location: {
    lat: number;
    long: number;
  };
}

interface Props {
  data: Cluster[];
}

// This is a gradient from blue through yellow to red
const colormap = interpolate([
  '#7CDDF8',
  '#95E3EC',
  '#ADE9E0',
  '#C7EFD3',
  '#E2F5C6',
  '#FFFCB8',
  '#FCDD9F',
  '#F8B782',
  '#F49769',
  '#F0724D',
  '#E7412A',
]);

// Interpolate the with a power function so that small numbers are more differentiated
function interpolateRatio(count: number, max: number): number {
  const ratio = Math.min(count / max, 1);
  return 1 - Math.pow(1 - ratio, 10);
}

type HeatmapFeatureProperties = { id: string; orderCount: number };
type HeatmapFeature = GeoJSON.Feature<GeoJSON.Point, HeatmapFeatureProperties>;

// These HAVE to be defined outside of the component, otherwise the component will continuously rerender
const superclusterOptions: Supercluster.Options<HeatmapFeatureProperties, HeatmapFeatureProperties> = {
  radius: 50,
  maxZoom: 14,
  minZoom: 4,
  reduce(acc, props) {
    acc.orderCount = (acc.orderCount || 0) + props.orderCount;
    return acc;
  },
};

export function Heatmap({ data }: Props): JSX.Element {
  const [loading, setLoading] = useState(true);
  const mapRef = useRef<MapRef>(null);
  const [postCodeFeatures, countryFeatures] = useMemo((): [HeatmapFeature[], HeatmapFeature[]] => {
    const postCodeFeatures: HeatmapFeature[] = [];
    const countryFeatures: HeatmapFeature[] = [];

    data.forEach((item) => {
      const feature: HeatmapFeature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.location.long, item.location.lat],
        },
        properties: {
          id: item.id,
          orderCount: item.count,
        },
      };

      if (item.isCountry) {
        countryFeatures.push(feature);
      } else {
        postCodeFeatures.push(feature);
      }
    });

    return [postCodeFeatures, countryFeatures];
  }, [data]);

  const totalCount: number = data.reduce((totalCount, item) => totalCount + item.count, 0);
  const maxCount = totalCount / 2;

  const [viewport, setViewport] = useState({
    zoom: 5,
    latitude: 51.1657,
    longitude: 10.4515,
  });

  const bounds = useMemo(() => {
    if (!mapRef.current) return;

    const bounds = mapRef.current.getBounds().toArray().flat();
    if (bounds.length !== 4) return;
    return bounds as [number, number, number, number];
  }, [viewport, mapRef.current]);

  const { clusters, supercluster } = useSupercluster<HeatmapFeatureProperties, HeatmapFeatureProperties>({
    points: postCodeFeatures,
    bounds,
    zoom: viewport.zoom,
    options: superclusterOptions,
  });

  const onClusterClick = ({
    clusterId,
    latitude,
    longitude,
  }: {
    clusterId: number;
    latitude: number;
    longitude: number;
  }): void => {
    if (!mapRef?.current || !supercluster) return;

    const nextZoom = supercluster.getClusterExpansionZoom(clusterId);
    mapRef.current.easeTo({
      center: [longitude, latitude],
      zoom: nextZoom,
      duration: 500,
    });
  };

  return (
    <Stack className="heatmap">
      {loading && <Spinner />}
      <Map
        mapStyle={'mapbox://styles/nerdgeschoss/clidw1y5c003l01pm0iwbabl4'}
        mapboxAccessToken={
          'pk.eyJ1IjoibmVyZGdlc2Nob3NzIiwiYSI6ImNsaWR2bmJzcDA4Z2IzbW5rM3d0eGVoeGsifQ.8Xglol-CbzedXpAr6t8Mwg'
        }
        {...viewport}
        onMove={(evt) => setViewport({ ...evt.viewState })}
        attributionControl={false}
        ref={mapRef}
        onRender={() => setLoading(false)}
        reuseMaps
      >
        {countryFeatures.map((feature) => {
          const { id, orderCount } = feature.properties;
          const [longitude, latitude] = feature.geometry.coordinates;

          return (
            <Marker key={id} latitude={latitude} longitude={longitude}>
              <div className="heatmap__marker heatmap__marker--country">{orderCount}</div>
            </Marker>
          );
        })}

        {clusters.map((cluster) => {
          const { id, orderCount } = cluster.properties;
          const [longitude, latitude] = cluster.geometry.coordinates;
          const ratio = interpolateRatio(orderCount, maxCount);
          const color = colormap(ratio);

          return (
            <Marker
              key={id}
              latitude={latitude}
              longitude={longitude}
              onClick={() => {
                if (!cluster.id || typeof cluster.id !== 'number') return;
                onClusterClick({ clusterId: cluster.id, latitude, longitude });
              }}
            >
              <div
                className="heatmap__marker heatmap__marker--cluster"
                style={{
                  backgroundColor: color,
                  width: `${15 + 50 * ratio}px`,
                  height: `${15 + 50 * ratio}px`,
                }}
              >
                {orderCount}
              </div>
            </Marker>
          );
        })}
      </Map>
    </Stack>
  );
}
